import React, {Fragment, useEffect, useState} from "react"
import {kebabCase, startCase} from "lodash"
import {faCircleNotch, faUpload} from "@fortawesome/free-solid-svg-icons"
import classNames from "classnames"
import {useModal} from "react-modal-hook"
import {useQueryState} from "use-location-state"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {useAccessToken, useIsUnitUser} from "../../services/auth"
import {RejectApplicationDocumentModal} from "./RejectApplicationDocument"
import {DocumentableAllowedToEvaluateUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {ApplicationDocumentModal} from "./ApplicationDocumentModal"
import LinkButton from "../LinkButton/LinkButton"
import {openLinkNewTab} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"
import {
	DataTable,
	DataTableActionHeader,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
} from "../DataTable/DataTable"
import {
	ApplicationDocument,
	ApplicationDocumentSide,
	ApplicationDocumentStatus,
	ApplicationDocumentType,
	ApplicationStatus,
	DocumentLink,
	documentEvaluationFlagDescriptions,
} from "../../resources/application"
import {
	DocumentableApplication,
	documentableApproveDocument,
	documentableGetDocumentLink,
	documentableUploadDocument,
} from "../../resources/documentable"
import {DocumentableHasPermission} from "../../containers/PermissionedUser/PermissionedUser"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {documentableHasPermission, useDocumentableIsAllowedToEvaluate} from "../../services/documentablePermission"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {AsyncResult} from "../../types/asyncResult"
import {TagsViewerKeyValue} from "../Tags/TagsViewer"
import {TagsViewerKeyOnly} from "../Tags/TagsViewerKeyOnly"
import {ArchivedToolTip} from "./ArchivedToolTip"

interface DocumentProps {
	document: ApplicationDocument
	application: DocumentableApplication
	accessToken: string
	refresh: () => void
}

const NO_UPLOAD_DOCUMENTS = ["SelfieVerification"]

function formatEvaluationFlag(flag: string): string {
	const trimmed = flag.trim()
	if (!trimmed) return ""
	return trimmed.endsWith(".") ? trimmed : `${trimmed}.`
}

function Document({document, application, accessToken, refresh}: DocumentProps) {
	const isUnitUser = useIsUnitUser()
	const isAllowedToEvaluate = useDocumentableIsAllowedToEvaluate(application, false)

	const [docId, setDocId] = useQueryState("docid", "")
	const [docSide, setDocSide] = useQueryState("side", "")

	const {addToast} = useToasts()

	const hasDocStorageId =
		document.attributes.status !== ApplicationDocumentStatus.Required &&
		!isUnitUser &&
		documentableHasPermission(application, "document.file", "get")
	const documentLink =
		!isUnitUser && document.attributes.status !== ApplicationDocumentStatus.Required
			? useAsyncResult(
					() => documentableGetDocumentLink(application, document.id, accessToken, ApplicationDocumentSide.Front),
					[hasDocStorageId]
			  )
			: AsyncResult.pending<OkDocument<DocumentLink>, ErrorDocument>()

	useEffect(() => {
		docId === document.id && docSide === "front" && showFrontDocModal()
		docId === document.id && docSide === "back" && showBackDocModal()
	}, [])

	async function openLinkinNewTab(side: ApplicationDocumentSide) {
		const response = await documentableGetDocumentLink(application, document.id, accessToken, side)
		response.match(
			(d) => openLinkNewTab(d.data.attributes.link),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)
	}

	const [showFrontDocModal, hideFrontDocModal] = useModal(() => (
		<ApplicationDocumentModal
			application={application}
			document={document}
			accessToken={accessToken}
			refresh={refresh}
			documentSide={ApplicationDocumentSide.Front}
			setDocSideQueryParam={setDocSide}
			close={() => {
				hideFrontDocModal()
				setDocId("")
				setDocSide("")
			}}
			addToast={addToast}
		/>
	))

	const [showBackDocModal, hideBackDocModal] = useModal(() => (
		<ApplicationDocumentModal
			application={application}
			document={document}
			accessToken={accessToken}
			refresh={refresh}
			documentSide={ApplicationDocumentSide.Back}
			setDocSideQueryParam={setDocSide}
			close={() => {
				hideBackDocModal()
				setDocId("")
				setDocSide("")
			}}
			addToast={addToast}
		/>
	))

	const [showReject, hideReject] = useModal(() => (
		<RejectApplicationDocumentModal
			close={hideReject}
			application={application}
			documentId={document.id}
			onSuccess={refresh}
		/>
	))
	const [isFileLoading, setIsFileLoading] = useState(false)

	const canApprove =
		application.attributes.status !== ApplicationStatus.Approved &&
		application.attributes.status !== ApplicationStatus.Denied &&
		application.attributes.status !== ApplicationStatus.Pending &&
		document.attributes.status !== ApplicationDocumentStatus.Approved

	const canReject =
		application.attributes.status !== ApplicationStatus.Approved &&
		application.attributes.status !== ApplicationStatus.Denied &&
		application.attributes.status !== ApplicationStatus.Pending &&
		document.attributes.status !== ApplicationDocumentStatus.Invalid

	let isUploadEnabled
	let documentSide: ApplicationDocumentSide = ApplicationDocumentSide.Front
	let statusClassname

	switch (document.attributes.status) {
		case ApplicationDocumentStatus.ReceivedBack:
		case ApplicationDocumentStatus.Required:
			statusClassname = "has-text-info"
			isUploadEnabled = true
			documentSide = ApplicationDocumentSide.Front

			break
		case ApplicationDocumentStatus.ReceivedFront:
			statusClassname = "has-text-info"
			isUploadEnabled = true
			documentSide = ApplicationDocumentSide.Back

			break
		case ApplicationDocumentStatus.Invalid:
			statusClassname = "has-text-danger"
			isUploadEnabled = true
			documentSide = ApplicationDocumentSide.Front
			break
		case ApplicationDocumentStatus.Approved:
			statusClassname = "has-text-success"
			isUploadEnabled = false
			break
		case ApplicationDocumentStatus.PendingReview:
			statusClassname = "has-text-warning"
			isUploadEnabled = false
			break
	}

	if (
		NO_UPLOAD_DOCUMENTS.includes(document.attributes.documentType) ||
		!documentableHasPermission(application, "document", "create")
	) {
		isUploadEnabled = false
	}

	const evaluationLinks: Array<JSX.Element> = []
	if (isAllowedToEvaluate) {
		if (isUnitUser) {
			//Only unit users can see evaluations in Alloy
			if (document.attributes.evaluationId != null) {
				evaluationLinks.push(
					<LinkButton
						key="evaluation"
						to={`https://unit.app.alloy.com/entities/${application.attributes.evaluationEntityId}/evaluations/${document.attributes.evaluationId}`}
					>
						Evaluation
					</LinkButton>
				)
			}
		}

		if (document.attributes.frontDocumentStoreId != null) {
			evaluationLinks.push(
				<LinkButton
					key="front"
					callback={() => {
						setDocId(document.id)
						setDocSide("front")
						showFrontDocModal()
					}}
				>
					Document
				</LinkButton>
			)
		} else if (document.attributes.frontDocumentId != null) {
			evaluationLinks.push(
				<LinkButton
					key="front"
					to={`https://unit.app.alloy.com/documents/${document.attributes.frontDocumentId}/download/`}
				>
					Document
				</LinkButton>
			)
		}

		if (document.attributes.backDocumentStoreId != null) {
			evaluationLinks.push(
				<LinkButton
					key="back"
					callback={() => {
						setDocId(document.id)
						setDocSide("back")
						showBackDocModal()
					}}
				>
					Back Document
				</LinkButton>
			)
		} else if (document.attributes.backDocumentId != null) {
			evaluationLinks.push(
				<LinkButton
					key="back"
					to={`https://unit.app.alloy.com/documents/${document.attributes.backDocumentId}/download/`}
				>
					Back Document
				</LinkButton>
			)
		}

		if (document.attributes.additionalDocumentStoreId != null) {
			evaluationLinks.push(
				<LinkButton
					key="additional"
					callback={() => {
						openLinkinNewTab(ApplicationDocumentSide.Additional)
					}}
				>
					{document.attributes.documentType === ApplicationDocumentType.selfieVerification
						? "Selfie"
						: "Additional Document"}
				</LinkButton>
			)
		} else if (document.attributes.additionalDocumentId != null) {
			evaluationLinks.push(
				<LinkButton
					key="additional"
					to={`https://unit.app.alloy.com/documents/${document.attributes.additionalDocumentId}/download/`}
				>
					{document.attributes.documentType === ApplicationDocumentType.selfieVerification
						? "Selfie"
						: "Additional Document"}
				</LinkButton>
			)
		}
	} else {
		if (hasDocStorageId && documentLink.isOk()) {
			evaluationLinks.push(
				<LinkButton
					key="front"
					callback={() => {
						setDocId(document.id)
						setDocSide("front")
						showFrontDocModal()
					}}
				>
					Document
				</LinkButton>
			)
		}
	}

	function onFileUploaded(e: React.ChangeEvent<HTMLInputElement>) {
		const files = e.target.files

		if (files) {
			const file = files[0]

			if (file) {
				setIsFileLoading(true)
				documentableUploadDocument(application, accessToken, document.id, file, file.type, documentSide)
					.then(() => {
						setIsFileLoading(false)
						refresh()
					})
					.catch(() => setIsFileLoading(false))
			}
		}
	}

	const isReasonCodeOther = document.attributes.reasonCode === "Other"

	const evaluationFlagDescriptions = documentEvaluationFlagDescriptions(document.attributes.evaluationFlags)
	const hasEvaluationFlags = evaluationFlagDescriptions.length > 0

	const evaluationFlagsTooltipContent = (
		<>
			<span className="status-tag-tooltip-header">Document evaluation remarks:</span>
			<span className="status-tag-tooltip-content">
				{evaluationFlagDescriptions.map((description, i) => (
					<Fragment key={`${description}-${i}`}>
						{i > 0 ? <br /> : null}
						{formatEvaluationFlag(description)}
					</Fragment>
				))}
			</span>
		</>
	)

	const invalidReasonTagValue = isReasonCodeOther ? "Other" : startCase(document.attributes.reasonCode) ?? ""

	const invalidReasonTooltipContent = isReasonCodeOther ? (
		<>
			<span className="status-tag-tooltip-header">Rejected document reason:</span>
			<span className="status-tag-tooltip-content">{document.attributes.reason}</span>
		</>
	) : undefined

	return (
		<DataTableRow className={document.attributes.archived ? "grayed-out" : ""}>
			<DataTableCell>{document.id}</DataTableCell>
			<DataTableCell>{startCase(document.attributes.documentType)}</DataTableCell>
			<DataTableCell className={statusClassname}>
				{hasEvaluationFlags ? (
					<TagsViewerKeyValue
						tags={{
							[startCase(document.attributes.status)]: "",
						}}
						keyClassName={kebabCase(`${document.attributes.status}-status-tag`)}
						valueClassName="status-tag-invalid-reason"
						tooltipContent={evaluationFlagsTooltipContent}
					/>
				) : document.attributes.status === ApplicationDocumentStatus.Invalid ? (
					<TagsViewerKeyValue
						tags={{
							[startCase(document.attributes.status)]: invalidReasonTagValue,
						}}
						keyClassName={kebabCase(`${document.attributes.status}-status-tag`)}
						valueClassName="status-tag-invalid-reason"
						tooltipContent={invalidReasonTooltipContent}
					/>
				) : (
					<TagsViewerKeyOnly
						keys={[startCase(document.attributes.status)]}
						keyClassName={kebabCase(`${document.attributes.status}-status-tag`)}
					/>
				)}
			</DataTableCell>
			<DataTableCell>{document.attributes.name}</DataTableCell>
			<DataTableCell>
				{isUploadEnabled && !document.attributes.archived && (
					<div className="file">
						<label className="file-label">
							<input
								disabled={application.attributes.status === ApplicationStatus.Pending}
								className="file-input"
								type="file"
								onChange={onFileUploaded}
								accept=".png,.jpg,.pdf,.heic,.heif"
							/>
							<span
								className={classNames("document-upload-button", "file-cta", {
									disabled: application.attributes.status === ApplicationStatus.Pending,
								})}
							>
								<span className="file-icon">
									<FontAwesomeIcon icon={isFileLoading ? faCircleNotch : faUpload} spin={isFileLoading} />
								</span>
								<span className="file-label">Choose a file…</span>
							</span>
						</label>
					</div>
				)}
			</DataTableCell>
			<DataTableCell>
				<DocumentableHasPermission application={application} resource="document.file" action="get">
					<div className="buttons are-small">{evaluationLinks}</div>
				</DocumentableHasPermission>
			</DataTableCell>
			<DataTableCell>
				{document.attributes.documentType === ApplicationDocumentType.clientRequested ? (
					<React.Fragment />
				) : document.attributes.archived ? (
					<ArchivedToolTip />
				) : (
					<DocumentableAllowedToEvaluateUserOnly application={application}>
						<div className="buttons are-small">
							<button
								className="button is-danger"
								disabled={!canReject}
								onClick={(e) => {
									e.preventDefault()
									showReject()
								}}
							>
								Reject
							</button>
							<button
								className="button is-success"
								disabled={!canApprove}
								onClick={(e) => {
									e.preventDefault()
									documentableApproveDocument(application, accessToken, document.id).then((result) => {
										if (result.isErr()) {
											addToast(result.error.errors[0].title, {appearance: "error"})
										}
										refresh()
									})
								}}
							>
								Approve
							</button>
						</div>
					</DocumentableAllowedToEvaluateUserOnly>
				)}
			</DataTableCell>
		</DataTableRow>
	)
}

export default function ApplicationDocuments({
	application,
	documents,
	refresh,
}: {
	application: DocumentableApplication
	documents: ApplicationDocument[]
	refresh: () => void
}) {
	const accessToken = useAccessToken()

	const rows = documents.map((d) => (
		<Document key={d.id} document={d} application={application} accessToken={accessToken} refresh={refresh} />
	))

	return (
		<DataTableCard className={"app-docs-card"}>
			<DataTableActionHeader title={"Documents"} />
			<div className={"app-docs-table"}>
				<DataTable hoverable={false} clickable={false} fullHeight={false}>
					<DataTableHead>
						<DataTableRow>
							<DataTableCell>Id</DataTableCell>
							<DataTableCell>Type</DataTableCell>
							<DataTableCell>Status</DataTableCell>
							<DataTableCell>For</DataTableCell>
							<DataTableCell>Upload Document</DataTableCell>
							<DataTableCell>
								<DocumentableHasPermission application={application} resource="document.file" action="get">
									Links
								</DocumentableHasPermission>
							</DataTableCell>
							<DataTableCell></DataTableCell>
						</DataTableRow>
					</DataTableHead>
					<tbody>{rows}</tbody>
				</DataTable>
			</div>
		</DataTableCard>
	)
}
